'use client';

import { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

export type ActionBarRoute = 'classes' | 'record' | null;

// Simplified context - manages route state and docking state
export interface ActionBarState {
  currentRoute: ActionBarRoute;
  isDocked: boolean;
  studentsCoveredByActionBar: number;
  totalStudentsAbsent: number;
  hideImages: boolean;
  /**
   * Distance in pixels from the viewport bottom to the top edge of the
   * visible action bar (0 when no action bar is rendered). Used by floating
   * bottom-anchored UI (e.g. minimized timer/stopwatch) to sit just above it.
   */
  actionBarBottomInset: number;
  /**
   * Width in pixels occupied by the sidebar on the left edge of the viewport
   * (0 when the sidebar is not rendered, e.g. on mobile). Lets
   * left-anchored floating UI avoid overlapping the sidebar.
   */
  sidebarLeftInset: number;
}

export interface ActionBarContextValue {
  state: ActionBarState;
  setCurrentRoute: (route: ActionBarRoute) => void;
  setIsDocked: (isDocked: boolean) => void;
  setStudentsCoveredByActionBar: (count: number) => void;
  setTotalStudentsAbsent: (count: number) => void;
  setHideImages: (hide: boolean) => void;
  setActionBarBottomInset: (inset: number) => void;
  setSidebarLeftInset: (inset: number) => void;
}

const ActionBarContext = createContext<ActionBarContextValue | undefined>(undefined);

const initialState: ActionBarState = {
  currentRoute: null,
  isDocked: false,
  studentsCoveredByActionBar: 0,
  totalStudentsAbsent: 0,
  hideImages: false,
  actionBarBottomInset: 0,
  sidebarLeftInset: 0,
};

export function ActionBarProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActionBarState>(initialState);

  const setCurrentRoute = useCallback((route: ActionBarRoute) => {
    setState(prev => ({ ...prev, currentRoute: route }));
  }, []);

  const setIsDocked = useCallback((isDocked: boolean) => {
    setState(prev => ({ ...prev, isDocked }));
  }, []);

  const setStudentsCoveredByActionBar = useCallback((count: number) => {
    setState(prev => ({ ...prev, studentsCoveredByActionBar: count }));
  }, []);

  const setTotalStudentsAbsent = useCallback((count: number) => {
    setState(prev => ({ ...prev, totalStudentsAbsent: count }));
  }, []);

  const setHideImages = useCallback((hide: boolean) => {
    setState(prev => ({ ...prev, hideImages: hide }));
  }, []);

  const setActionBarBottomInset = useCallback((inset: number) => {
    setState(prev => (prev.actionBarBottomInset === inset ? prev : { ...prev, actionBarBottomInset: inset }));
  }, []);

  const setSidebarLeftInset = useCallback((inset: number) => {
    setState(prev => (prev.sidebarLeftInset === inset ? prev : { ...prev, sidebarLeftInset: inset }));
  }, []);

  const value: ActionBarContextValue = useMemo(() => ({
    state,
    setCurrentRoute,
    setIsDocked,
    setStudentsCoveredByActionBar,
    setTotalStudentsAbsent,
    setHideImages,
    setActionBarBottomInset,
    setSidebarLeftInset,
  }), [state, setCurrentRoute, setIsDocked, setStudentsCoveredByActionBar, setTotalStudentsAbsent, setHideImages, setActionBarBottomInset, setSidebarLeftInset]);
  
  return (
    <ActionBarContext.Provider value={value}>
      {children}
    </ActionBarContext.Provider>
  );
}

export function useActionBarContext() {
  const context = useContext(ActionBarContext);
  if (context === undefined) {
    throw new Error('useActionBarContext must be used within an ActionBarProvider');
  }
  return context;
}

/**
 * Safe version of useActionBarContext that returns default values
 * when used outside of ActionBarProvider. Use this in hooks that may
 * be called from components not wrapped in the provider.
 */
export function useActionBarContextSafe(): ActionBarContextValue {
  const context = useContext(ActionBarContext);
  if (context === undefined) {
    // Return default values when outside provider
    return {
      state: { currentRoute: null, isDocked: false, studentsCoveredByActionBar: 0, totalStudentsAbsent: 0, hideImages: false, actionBarBottomInset: 0, sidebarLeftInset: 0 },
      setCurrentRoute: () => {},
      setIsDocked: () => {},
      setStudentsCoveredByActionBar: () => {},
      setTotalStudentsAbsent: () => {},
      setHideImages: () => {},
      setActionBarBottomInset: () => {},
      setSidebarLeftInset: () => {},
    };
  }
  return context;
}